import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * Service Coverage Chart
 *
 * Shows service coverage as a color-coded horizontal bar chart
 */
function ServiceCoverageChart({ data, title, narrative, templateText }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm">
          No service coverage data available.
        </p>
      </div>
    )
  }

  // Sort data by percentage descending
  const sortedData = [...data].sort((a, b) => b.percentage - a.percentage)

  const chartOption = {
    title: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: function (params) {
        const item = params[0]
        const service = sortedData[item.dataIndex]
        return `
          <b>${service.name}</b><br/>
          Coverage: ${service.percentage}%<br/>
          Facilities: ${service.count}/${service.total}
        `
      },
      fontFamily:
        'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    },
    grid: {
      left: '25%',
      right: '10%',
      top: '5%',
      bottom: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      max: 100,
      name: 'Coverage %',
      nameTextStyle: {
        fontSize: 12,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      axisLabel: {
        formatter: '{value}%',
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { lineStyle: { type: 'dashed' } },
    },
    yAxis: {
      type: 'category',
      data: sortedData.map(s => s.name),
      axisLabel: {
        fontSize: 11,
        fontFamily:
          'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      },
      splitLine: { show: false },
    },
    series: [
      {
        name: 'Coverage',
        type: 'bar',
        data: sortedData.map(s => ({
          value: s.percentage,
          itemStyle: { color: s.color },
        })),
        label: {
          show: true,
          position: 'right',
          formatter: '{c}%',
          fontSize: 11,
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
        },
        barMaxWidth: 30,
      },
    ],
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>

      {/* Narrative or template text */}
      {(narrative || templateText) && (
        <div className="mb-4">
          <p className="text-gray-700 text-sm leading-relaxed">
            {narrative || templateText}
          </p>
        </div>
      )}

      {/* Chart */}
      <div className="h-96">
        <ReactECharts
          option={chartOption}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          theme="ug-dashboard"
        />
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-600 rounded"></div>
          <span className="text-gray-700">Good (≥80%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-yellow-500 rounded"></div>
          <span className="text-gray-700">Moderate (50-79%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-600 rounded"></div>
          <span className="text-gray-700">Poor (&lt;50%)</span>
        </div>
      </div>
    </div>
  )
}

ServiceCoverageChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      percentage: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      total: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  narrative: PropTypes.string,
  templateText: PropTypes.string,
}

export default ServiceCoverageChart
